import { useCallback, useEffect, useState } from 'react';
import { isHttpError, useHttp } from '@mono/http-hook';
import useSession from '../../session/useSession';
import type { OrganizationRoleValue } from '../../session/useSession';
import { useCurrentOrganization } from '../../organizations/hooks';

export type OrganizationUser = {
  id: string;
  role: OrganizationRoleValue;
  joinedAt: string;
  user: {
    id: string;
    name: string | null;
    email: string | null;
  };
};

export type CreateUserInput = {
  email: string;
  role: OrganizationRoleValue;
};

export type UpdateUserInput = {
  role: OrganizationRoleValue;
};

type UsersResponse = {
  members: OrganizationUser[];
};

type UserResponse = {
  member: OrganizationUser;
};

const getHttpErrorMessage = (error: unknown, fallback: string) => {
  if (isHttpError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as {
          error?: string;
          message?: string;
        };
      return errorMessage ?? responseMessage ?? error.message ?? fallback;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const sortUsers = (users: OrganizationUser[]) =>
  [...users].sort((a, b) => {
    const nameA = a.user.name ?? a.user.email ?? '';
    const nameB = b.user.name ?? b.user.email ?? '';
    return nameA.localeCompare(nameB);
  });

export const useUsersSettings = () => {
  const http = useHttp();
  const session = useSession();
  const currentOrganization = useCurrentOrganization();
  const organizationId = currentOrganization?.id ?? '';

  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(organizationId));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const [mutateError, setMutateError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!organizationId) {
      setUsers([]);
      setIsLoading(false);
      setLoadError(null);
      setMutateError(null);
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    try {
      const { data } = await http.get<UsersResponse>(
        `/api/v1/organizations/${organizationId}/users`,
        { withCredentials: true }
      );

      setUsers(sortUsers(data.members));
      setMutateError(null);
    } catch (error) {
      setUsers([]);
      setMutateError(null);
      setLoadError(getHttpErrorMessage(error, 'Unable to load users right now.'));
    } finally {
      setIsLoading(false);
    }
  }, [http, organizationId]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  const createUser = useCallback(
    async (input: CreateUserInput) => {
      if (!organizationId) {
        throw new Error('Organization is not selected.');
      }

      setMutateError(null);
      setIsMutating(true);

      try {
        const { data } = await http.post<UserResponse>(
          `/api/v1/organizations/${organizationId}/users`,
          input,
          { withCredentials: true }
        );

        setUsers((prev) => sortUsers([...prev, data.member]));
        await session.update?.();
        return data.member;
      } catch (error) {
        const message = getHttpErrorMessage(error, 'Unable to add user.');
        setMutateError(message);
        throw new Error(message);
      } finally {
        setIsMutating(false);
      }
    },
    [http, organizationId, session]
  );

  const updateUser = useCallback(
    async (memberId: string, input: UpdateUserInput) => {
      if (!organizationId) {
        throw new Error('Organization is not selected.');
      }

      setMutateError(null);
      setIsMutating(true);

      try {
        const { data } = await http.patch<UserResponse>(
          `/api/v1/organizations/${organizationId}/users/${memberId}`,
          input,
          { withCredentials: true }
        );

        setUsers((prev) =>
          sortUsers(prev.map((member) => (member.id === memberId ? data.member : member)))
        );
        await session.update?.();
        return data.member;
      } catch (error) {
        const message = getHttpErrorMessage(error, 'Unable to update user.');
        setMutateError(message);
        throw new Error(message);
      } finally {
        setIsMutating(false);
      }
    },
    [http, organizationId, session]
  );

  const deleteUser = useCallback(
    async (memberId: string) => {
      if (!organizationId) {
        throw new Error('Organization is not selected.');
      }

      setMutateError(null);
      setIsMutating(true);

      try {
        await http.delete(
          `/api/v1/organizations/${organizationId}/users/${memberId}`,
          { withCredentials: true }
        );

        setUsers((prev) => prev.filter((member) => member.id !== memberId));
        await session.update?.();
      } catch (error) {
        const message = getHttpErrorMessage(error, 'Unable to remove user.');
        setMutateError(message);
        throw new Error(message);
      } finally {
        setIsMutating(false);
      }
    },
    [http, organizationId, session]
  );

  return {
    organizationId,
    users,
    isLoading,
    loadError,
    refresh: fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    isMutating,
    mutateError,
  };
};

export const useOrganizationUsersList = () => {
  const http = useHttp();
  const currentOrganization = useCurrentOrganization();
  const organizationId = currentOrganization?.id ?? '';

  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(organizationId));
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!organizationId) {
      setUsers([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data } = await http.get<UsersResponse>(
        `/api/v1/organizations/${organizationId}/users`,
        { withCredentials: true }
      );

      setUsers(sortUsers(data.members));
    } catch (error) {
      setUsers([]);
      setError(getHttpErrorMessage(error, 'Unable to load users right now.'));
    } finally {
      setIsLoading(false);
    }
  }, [http, organizationId]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  return {
    organizationId,
    users,
    isLoading,
    error,
    refresh: fetchUsers,
  };
};
